import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import RowsIndicator from './common/RowsIndicator';
import StreetAutocompleteInput from './common/StreetAutocompleteInput';
import Icon13 from "../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { FiFilter, FiPlus, FiDownload, FiColumns } from 'react-icons/fi';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import { fetchPicklists } from './common/constants';
import axios from 'axios';

// Import shared components
import { ColumnFilterDropdown, useFilterPopupPortal } from './common/filters';
import { TooltipCell, ResetButton, ColumnVisibilityDropdown, SortIcon, sortIconDirection } from './common';
import { formatDate } from '../utils';
import { loadXLSX } from '../utils/xlsxLoader';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useUserPreference } from '../hooks/useUserPreference';
import { USER_PREFERENCE_KEYS } from '../constants/userPreferences';
import './AddAccountModal.css';

const ACCOUNT_CLOSE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
  </svg>
);

const ACCOUNT_MODAL_FORM_ID = 'account-modal-form';

const ITEMS_PER_PAGE = 15;
const INITIAL_LOAD_COUNT = 15;

// Add CSS styles for select dropdowns
const selectStyles = {
  select: {
    padding: '0.5rem',
    border: '1px solid #e5e7eb',
    borderRadius: '0.25rem',
    width: '100%',
    height: '40px',
    backgroundColor: 'white'
  },
  option: {
    padding: '8px',
    cursor: 'pointer'
  }
};

// Custom hook to handle select dropdown
const useSelectDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
    // Force browser to show only 10 items
    setTimeout(() => {
      const selects = document.querySelectorAll('select');
      selects.forEach(select => {
        if (select.size === 0) {
          select.size = 10;
        }
      });
    }, 0);
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset size when closing
    const selects = document.querySelectorAll('select');
    selects.forEach(select => {
      select.size = 0;
    });
  };

  return { isOpen, handleOpen, handleClose };
};

const US_STATE_OPTIONS = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

// Helper to normalize and map various state representations to a 2-letter US code
const toUSStateCode = (input) => {
  if (!input) return '';
  const trimmed = String(input).trim();
  if (trimmed.length === 2) {
    // Already a code; ensure it's valid
    const upper = trimmed.toUpperCase();
    return US_STATE_OPTIONS.some(s => s.code === upper) ? upper : '';
  }
  // Match by full name (case-insensitive)
  const lower = trimmed.toLowerCase();
  const match = US_STATE_OPTIONS.find(s => s.name.toLowerCase() === lower);
  return match ? match.code : '';
};

// TooltipCell is now imported from './common'

// Format ISO date to MM/DD/YYYY, h:mm:ss am/pm
const formatFullTimestamp = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${mm}/${dd}/${yyyy}, ${hours}:${minutes}:${seconds} ${ampm}`;
};

const DEFAULT_COLUMN_ORDER = [
  'Name',
  'Type',
  'GMB_Region__c',
  'BillingCity',
  'BillingState',
  'BillingPostalCode',
  'Project_Site_Acct_Last_Modified_By__c',
  'Project_Site_Acct_Last_Modified_Date__c',
  'BillingCountry',
  'BillingStreet',
  'Company_Name__c',
  'Fax',
  'Parent.Name',
  'Phone',
  'ShippingCity',
  'ShippingCountry',
  'ShippingPostalCode',
  'ShippingState',
  'ShippingStreet',
  'Website'
];

const DEFAULT_VISIBLE_COLUMNS = [
  'Name',
  'Type',
  'GMB_Region__c',
  'BillingCity',
  'BillingState',
  'BillingPostalCode',
  'Project_Site_Acct_Last_Modified_By__c',
  'Project_Site_Acct_Last_Modified_Date__c'
];

function mergeColumnOrder(savedOrder) {
  if (!Array.isArray(savedOrder)) {
    return DEFAULT_COLUMN_ORDER;
  }

  const merged = [...savedOrder];
  DEFAULT_COLUMN_ORDER.forEach((key) => {
    if (!merged.includes(key)) {
      merged.push(key);
    }
  });
  return merged.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

function mergeVisibleColumns(savedColumns) {
  if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }
  return savedColumns.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

const DEFAULT_PINNED_COLUMNS = [];

function mergePinnedColumns(savedPins) {
  if (!Array.isArray(savedPins)) {
    return DEFAULT_PINNED_COLUMNS;
  }
  return savedPins.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

const originalColumns = [
  { key: 'Name', label: 'Name', filterable: true, width: 220 },
  { key: 'Type', label: 'Type', filterable: true, width: 150 },
  { key: 'GMB_Region__c', label: 'Region', filterable: true, width: 150 },
  { key: 'BillingCity', label: 'Billing City', filterable: true, width: 160 },
  { key: 'BillingState', label: 'Billing State', filterable: true, width: 130 },
  { key: 'BillingPostalCode', label: 'Billing Zipcode', filterable: true, width: 140 },
  { key: 'Project_Site_Acct_Last_Modified_By__c', label: 'Last Modified By', filterable: true, width: 180 },
  { key: 'Project_Site_Acct_Last_Modified_Date__c', label: 'Last Modified Date', filterable: true, width: 180 },
  { key: 'BillingCountry', label: 'Billing Country', filterable: true, width: 150 },
  { key: 'BillingStreet', label: 'Billing Street', filterable: true, width: 240 },
  { key: 'Company_Name__c', label: 'Company Name', filterable: true, width: 200 },
  { key: 'Fax', label: 'Fax', filterable: true, width: 140 },
  { key: 'Parent.Name', label: 'Parent Account', filterable: true, width: 220 },
  { key: 'Phone', label: 'Phone', filterable: true, width: 140 },
  { key: 'ShippingCity', label: 'Shipping City', filterable: true, width: 160 },
  { key: 'ShippingCountry', label: 'Shipping Country', filterable: true, width: 150 },
  { key: 'ShippingPostalCode', label: 'Shipping Zipcode', filterable: true, width: 140 },
  { key: 'ShippingState', label: 'Shipping State', filterable: true, width: 130 },
  { key: 'ShippingStreet', label: 'Shipping Street', filterable: true, width: 240 },
  { key: 'Website', label: 'Website', filterable: true, width: 200 },
];

// ColumnFilterDropdown is now imported from './common/filters'

export function AddAccountModal({ isOpen, onClose, onSuccess, account }) {
  const { authFetch, getAuthConfig, isReady } = useAuthFetch();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    companyName: '',
    type: '',
    region: '',
    website: '',
    noWebsite: false,
    billing: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    },
    shipping: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    },
    parentAccount: '',
    parentAccountId: ''
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isAccountDropdownVisible, setIsAccountDropdownVisible] = useState(false);
  const [isNameDropdownVisible, setIsNameDropdownVisible] = useState(false);
  const [accountContacts, setAccountContacts] = useState([]);
  const [nameSearchResults, setNameSearchResults] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [isExistingAccount, setIsExistingAccount] = useState(false);
  const [noMatchFound, setNoMatchFound] = useState(false);
  const billingSelect = useSelectDropdown();
  const shippingSelect = useSelectDropdown();
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [typeOptions, setTypeOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);

  useEffect(() => {
    fetchPicklists()
      .then(({ typeOptions, regionOptions }) => {
        setTypeOptions(typeOptions);
        setRegionOptions(regionOptions);
      })
      .catch(err => {
        console.error("Failed to load picklists:", err);
      });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);


  useEffect(() => {
    if (isOpen && account) {
      // When account data is passed, populate the form
      setForm({
        name: account.Name || '',
        phone: account.Phone || '',
        companyName: account.Company_Name__c || '',
        type: account.Type || '',
        region: account.GMB_Region__c || '',
        website: account.Website || '',
        noWebsite: account.No_Website__c || false,
        billing: {
          street: account.BillingStreet || '',
          city: account.BillingCity || '',
          state: account.BillingState || '',
          postalCode: account.BillingPostalCode || '',
          country: account.BillingCountry || 'US',
        },
        shipping: {
          street: account.ShippingStreet || '',
          city: account.ShippingCity || '',
          state: account.ShippingState || '',
          postalCode: account.ShippingPostalCode || '',
          country: account.ShippingCountry || 'US',
        },
        parentAccount: account.Parent?.Name || '',
        parentAccountId: account.ParentId || '',
      });
      // Set the search terms to match the account data
      setNameSearchTerm(account.Name || '');
      setAccountSearchTerm(account.Parent?.Name || '');
      setSelectedAccount(account);
      setIsExistingAccount(true);
      setNoMatchFound(false);
    } else if (!isOpen) {
      // Reset form and search terms when modal is closed
      setForm({
        name: '',
        phone: '',
        companyName: '',
        type: '',
        region: '',
        website: '',
        noWebsite: false,
        billing: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US'
        },
        shipping: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US'
        },
        parentAccount: '',
        parentAccountId: ''
      });
      setNameSearchTerm('');
      setAccountSearchTerm('');
      setSuccess(false);
      setError('');
      setSelectedAccount(null);
      setIsExistingAccount(false);
      setNoMatchFound(false);
    }
  }, [isOpen, account]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNameSearch = async (e) => {
    const searchTerm = e.target.value;
    setNameSearchTerm(searchTerm);
    setForm(prev => ({ ...prev, name: searchTerm }));
    setNoMatchFound(false);
    setIsExistingAccount(false);
    setError('');
    setSelectedAccount(null);

    if (searchTerm.trim().length < 3) {
      setIsNameDropdownVisible(false);
      setNameSearchResults([]);
      setForm(prev => ({
        ...prev,
        phone: '',
        companyName: '',
        type: '',
        region: '',
        website: '',
        noWebsite: false,
        billing: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US'
        },
        shipping: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US'
        },
        parentAccount: '',
        parentAccountId: ''
      }));
      return;
    }

    try {
      if (!isReady) return;
      // Manually encode the search parameter to ensure spaces are encoded as %20
      const encodedSearch = encodeURIComponent(searchTerm);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/accounts/name/${encodedSearch}`,
        await getAuthConfig()
      );
      const records = response.data.records;

      // Handle different response structures
      let searchResults = [];

      if (records.message === "No record found for the given account name") {
        // No records found
        searchResults = [];
      } else if (records.message === "Multiple records found for the given account name" && Array.isArray(records.records)) {
        // Multiple records found
        searchResults = records.records;
      } else if (records.Id && records.Name) {
        // Single record found (direct object)
        searchResults = [records];
      } else if (Array.isArray(records.records)) {
        // Multiple records (array structure)
        searchResults = records.records;
      }

      if (searchResults.length === 0) {
        setNameSearchResults([]);
        setIsNameDropdownVisible(false);
        setNoMatchFound(true);
        setIsExistingAccount(false);
        setError('');
        setSelectedAccount(null);
        setForm(prev => ({
          ...prev,
          phone: '',
          companyName: '',
          type: '',
          region: '',
          website: '',
          noWebsite: false,
          billing: {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'US'
          },
          shipping: {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'US'
          },
          parentAccount: '',
          parentAccountId: ''
        }));
      } else {
        setNameSearchResults(searchResults);
        setIsNameDropdownVisible(true);
        setIsExistingAccount(true);
        setNoMatchFound(false);
      }
    } catch (error) {
      console.error("Error searching accounts:", error);
      setNameSearchResults([]);
      setIsNameDropdownVisible(false);
      setNoMatchFound(true);
      setIsExistingAccount(false);
      setError('');
      setSelectedAccount(null);
      setForm(prev => ({
        ...prev,
        phone: '',
        companyName: '',
        type: '',
        region: '',
        website: '',
        noWebsite: false,
        billing: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US'
        },
        shipping: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US'
        },
        parentAccount: '',
        parentAccountId: ''
      }));
    }
  };

  const handleNameSelect = (selectedAccount) => {
    setForm(prev => ({
      ...prev,
      name: selectedAccount.Name || '',
      phone: selectedAccount.Phone || '',
      companyName: selectedAccount.Company_Name__c || '',
      type: selectedAccount.Type || '',
      region: selectedAccount.GMB_Region__c || '',
      website: selectedAccount.Website || '',
      noWebsite: selectedAccount.No_Website__c || false,
      parentAccount: selectedAccount.Parent?.Name || '',
      parentAccountId: selectedAccount.Parent?.Id || '',
      billing: {
        street: selectedAccount.BillingStreet || '',
        city: selectedAccount.BillingCity || '',
        state: selectedAccount.BillingState || '',
        postalCode: selectedAccount.BillingPostalCode || '',
        country: selectedAccount.BillingCountry || 'United States',
      },
      shipping: {
        street: selectedAccount.ShippingStreet || '',
        city: selectedAccount.ShippingCity || '',
        state: selectedAccount.ShippingState || '',
        postalCode: selectedAccount.ShippingPostalCode || '',
        country: selectedAccount.ShippingCountry || 'United States',
      }
    }));
    setNameSearchTerm(selectedAccount.Name || '');
    setIsNameDropdownVisible(false);
    setSelectedAccount(selectedAccount);
    setIsExistingAccount(true);
    setNoMatchFound(false);
    setError('');
  };

  const handleAccountSearch = async (e) => {
    const searchTerm = e.target.value;
    setAccountSearchTerm(searchTerm);
    setForm(prev => ({ ...prev, parentAccount: searchTerm, parentAccountId: '' }));

    if (searchTerm.trim().length < 3) {
      setIsAccountDropdownVisible(false);
      setAccountContacts([]);
      return;
    }

    try {
      if (!isReady) return;
      // Manually encode the search parameter to ensure spaces are encoded as %20
      const encodedSearch = encodeURIComponent(searchTerm);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/accounts/name/${encodedSearch}`,
        await getAuthConfig()
      );
      const records = response.data.records;

      // Handle different response structures
      let searchResults = [];

      if (records.message === "No record found for the given account name") {
        // No records found
        searchResults = [];
      } else if (records.message === "Multiple records found for the given account name" && Array.isArray(records.records)) {
        // Multiple records found
        searchResults = records.records;
      } else if (records.Id && records.Name) {
        // Single record found (direct object)
        searchResults = [records];
      } else if (Array.isArray(records.records)) {
        // Multiple records (array structure)
        searchResults = records.records;
      }

      if (searchResults.length === 0) {
        setAccountContacts([]);
        setIsAccountDropdownVisible(false);
      } else {
        setAccountContacts(searchResults);
        setIsAccountDropdownVisible(true);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccountContacts([]);
      setIsAccountDropdownVisible(false);
    }
  };

  const handleAccountSelect = (account) => {
    setForm(prev => ({
      ...prev,
      parentAccount: account.Name,
      parentAccountId: account.Id
    }));
    setIsAccountDropdownVisible(false);
  };

  const handleAddressChange = (type, field, value) => {
    setForm(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const handleWebsiteCheckbox = (checked) => {
    setForm(prev => ({
      ...prev,
      noWebsite: checked,
      website: checked ? '' : prev.website
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check each required field individually
    if (!form.name.trim()) {
      setError('Account Name is required.');
      return;
    }

    if (!form.type.trim()) {
      setError('Type is required.');
      return;
    }

    if (!form.region.trim()) {
      setError('Region is required.');
      return;
    }

    if (!form.billing.state.trim()) {
      setError('Billing State is required.');
      return;
    }

    if (!form.noWebsite && !form.website.trim()) {
      setError('Website is required.');
      return;
    }

    setError('');

    try {
      if (!isReady) return;
      const requestBody = {
        Name: form.name,
        ParentId: form.parentAccountId || null,
        Company_Name__c: form.companyName,
        Phone: form.phone,
        Website: form.website,
        No_Website__c: form.noWebsite,
        Type: form.type,
        GMB_Region__c: form.region,
        BillingStreet: form.billing.street,
        BillingCity: form.billing.city,
        BillingState: form.billing.state,
        BillingPostalCode: form.billing.postalCode,
        BillingCountry: form.billing.country,
        Project_Site_Acct_Last_Modified_By__c: (window.__currentUserDisplayName || ''),
        Project_Site_Acct_Last_Modified_Date__c: new Date().toISOString(),
        ShippingStreet: form.shipping.street,
        ShippingCity: form.shipping.city,
        ShippingState: form.shipping.state,
        ShippingPostalCode: form.shipping.postalCode,
        ShippingCountry: form.shipping.country
      };

      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/accounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to add account');
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onSuccess();
      }, 1000);
    } catch (error) {
      console.error('Error adding account:', error);
      setError('Failed to add account. Please try again.');
    }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();

    // Check required fields
    if (!form.name.trim()) {
      setError('Account Name is required.');
      return;
    }

    if (!form.type.trim()) {
      setError('Type is required.');
      return;
    }

    if (!form.region.trim()) {
      setError('Region is required.');
      return;
    }

    if (!form.billing.state.trim()) {
      setError('Billing State is required.');
      return;
    }

    if (!form.noWebsite && !form.website.trim()) {
      setError('Website is required unless marked as no website');
      return;
    }

    setError('');

    try {
      if (!isReady) return;
      const requestBody = {
        Name: form.name,
        ParentId: form.parentAccountId || null,
        Company_Name__c: form.companyName || null,
        Phone: form.phone || null,
        Website: form.website || null,
        No_Website__c: form.noWebsite || false,
        Type: form.type,
        GMB_Region__c: form.region,
        BillingStreet: form.billing.street || null,
        BillingCity: form.billing.city || null,
        BillingState: form.billing.state,
        BillingPostalCode: form.billing.postalCode || null,
        BillingCountry: form.billing.country || 'United States',
        Project_Site_Acct_Last_Modified_By__c: (window.__currentUserDisplayName || ''),
        Project_Site_Acct_Last_Modified_Date__c: new Date().toISOString(),
        ShippingStreet: form.shipping.street || null,
        ShippingCity: form.shipping.city || null,
        ShippingState: form.shipping.state || null,
        ShippingPostalCode: form.shipping.postalCode || null,
        ShippingCountry: form.shipping.country || 'United States'
      };

      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/accounts/${selectedAccount.Id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to update account');
      }

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onSuccess();
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to update account');
      }
    } catch (error) {
      console.error('Error updating account:', error);
      setError('Failed to update account. Please try again.');
    }
  };

  if (!isOpen) return null;

  const modalTitle = selectedAccount ? 'Update Account' : 'Add Account';
  const submitLabel = selectedAccount ? 'Update Account' : 'Add Account';

  return createPortal(
    <div className="account-modal-overlay">
      <div className="account-modal-sheet">
        <div className="account-modal-header">
          <h2 className="account-modal-title">{modalTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="account-modal-close"
            aria-label="Close"
          >
            {ACCOUNT_CLOSE_ICON}
          </button>
        </div>

        <div className={`account-modal-body${success ? ' account-modal-body--success' : ''}`}>
          {success ? (
            <>
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-lg font-semibold text-gray-700 mb-2">
                {selectedAccount ? 'Account Updated!' : 'Account Added!'}
              </div>
            </>
          ) : (
            <form
              id={ACCOUNT_MODAL_FORM_ID}
              onSubmit={selectedAccount ? handleUpdateAccount : handleSubmit}
              className="space-y-4"
            >
              {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
              {/* Account Name */}
              <div className="account-modal-form-grid account-modal-form-grid-2">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Account Name<span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={nameSearchTerm}
                        onChange={handleNameSearch}
                        placeholder="Search or enter account name"
                        className="w-full p-2 border rounded"
                        required
                      />
                      <img src={Icon13} alt="Search Icon" className="absolute right-2 w-5 h-5 cursor-pointer" />
                    </div>
                    {isNameDropdownVisible && nameSearchResults.length > 0 && (
                      <div className="absolute top-full left-0 bg-white border rounded-md mt-1 w-full shadow-lg z-10 max-h-48 overflow-y-auto account-modal-search-dropdown">
                        <ul>
                          {nameSearchResults.map((acc) => (
                            <li
                              key={acc.Id}
                              className="p-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => handleNameSelect(acc)}
                            >
                              {acc.Name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {noMatchFound && (
                      <div className="text-sm text-green-600 mt-1">
                        No existing account found. You can create a new one.
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Parent Account</label>
                  <div className="relative">
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={accountSearchTerm}
                        onChange={handleAccountSearch}
                        placeholder="Search by account name"
                        className="w-full p-2 border rounded"
                        disabled={isExistingAccount}
                      />
                      <img src={Icon13} alt="Search Icon" className="absolute right-2 w-5 h-5 cursor-pointer" />
                    </div>
                    {isAccountDropdownVisible && accountContacts.length > 0 && (
                      <div className="absolute top-full left-0 bg-white border rounded-md mt-1 w-full shadow-lg z-10 max-h-48 overflow-y-auto account-modal-search-dropdown">
                        <ul>
                          {accountContacts.map((acc) => (
                            <li
                              key={acc.Id}
                              className="p-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => handleAccountSelect(acc)}
                            >
                              {acc.Name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Phone, Company Name, Website */}
              <div className="account-modal-form-grid account-modal-form-grid-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Phone</label>
                  <input
                    className="w-full p-2 border rounded"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Company Name</label>
                  <input
                    className="w-full p-2 border rounded"
                    value={form.companyName}
                    onChange={e => handleChange('companyName', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Website<span className="text-red-500">*</span></label>
                  <div className="flex items-center gap-4 md:flex-row max-md:flex-col max-md:items-start">
                    <input
                      className="flex-1 p-2 border rounded w-full"
                      type="text"
                      placeholder="Enter website URL"
                      value={form.website}
                      onChange={e => handleChange('website', e.target.value)}
                      disabled={form.noWebsite}
                    />
                    <label className="flex items-center gap-2 whitespace-nowrap max-md:mt-2">
                      <input
                        type="checkbox"
                        checked={form.noWebsite}
                        onChange={(e) => handleWebsiteCheckbox(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-600">No website exists</span>
                    </label>
                  </div>
                </div>
              </div>
              {/* Type, Region */}
              <div className="account-modal-form-grid account-modal-form-grid-2">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Type<span className="text-red-500">*</span></label>
                  <select
                    className="w-full p-2 border rounded"
                    value={form.type}
                    onChange={e => handleChange('type', e.target.value)}
                    required
                  >
                    <option value="">Select Type</option>
                    {typeOptions.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Region<span className="text-red-500">*</span></label>
                  <select
                    className="w-full p-2 border rounded"
                    value={form.region}
                    onChange={e => handleChange('region', e.target.value)}
                    required
                  >
                    <option value="">Select Region</option>
                    {regionOptions.map(region => <option key={region} value={region}>{region}</option>)}
                  </select>
                </div>
              </div>
              {/* Billing Address & Shipping Address */}
              <div className="account-modal-form-grid account-modal-form-grid-2 account-modal-form-grid-addresses">
                {/* Billing Address */}
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Billing Address</label>
                  <StreetAutocompleteInput
                    value={form.billing.street}
                    onChange={(text) => handleAddressChange('billing', 'street', text)}
                    onSelectSuggestion={(s) => handleAddressChange('billing', 'street', s.description || s.main_text || '')}
                    onPlaceDetails={(d) => {
                      const street = [d.street_number, d.route].filter(Boolean).join(' ');
                      setForm(prev => ({
                        ...prev,
                        billing: {
                          ...prev.billing,
                          street: street || prev.billing.street,
                          city: d.city || prev.billing.city,
                          state: d.country_code === 'US' ? (toUSStateCode(d.state) || prev.billing.state) : prev.billing.state,
                          postalCode: d.postal_code || prev.billing.postalCode,
                          country: d.country_code || prev.billing.country,
                        }
                      }));
                    }}
                    placeholder="Street"
                  />
                  <div className="account-modal-address-fields">
                    <input
                      className="p-2 border rounded"
                      placeholder="City"
                      value={form.billing.city}
                      onChange={e => handleAddressChange('billing', 'city', e.target.value)}
                    />
                    <select
                      value={form.billing.state}
                      onChange={e => handleAddressChange('billing', 'state', e.target.value)}
                      required
                    >
                      <option value="">Select State*</option>
                      {US_STATE_OPTIONS.map(state => (
                        <option
                          key={state.code}
                          value={state.code}
                        >
                          {state.code} - {state.name}
                        </option>
                      ))}
                    </select>
                    <input
                      className="p-2 border rounded"
                      placeholder="Zip/Postal Code"
                      value={form.billing.postalCode}
                      onChange={e => handleAddressChange('billing', 'postalCode', e.target.value)}
                    />
                  </div>
                  <input
                    className="p-2 border rounded w-full bg-gray-100"
                    value={form.billing.country}
                  />
                </div>
                {/* Shipping Address */}
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Shipping Address</label>
                  <StreetAutocompleteInput
                    value={form.shipping.street}
                    onChange={(text) => handleAddressChange('shipping', 'street', text)}
                    onSelectSuggestion={(s) => handleAddressChange('shipping', 'street', s.description || s.main_text || '')}
                    onPlaceDetails={(d) => {
                      const street = [d.street_number, d.route].filter(Boolean).join(' ');
                      setForm(prev => ({
                        ...prev,
                        shipping: {
                          ...prev.shipping,
                          street: street || prev.shipping.street,
                          city: d.city || prev.shipping.city,
                          state: d.country_code === 'US' ? (toUSStateCode(d.state) || prev.shipping.state) : prev.shipping.state,
                          postalCode: d.postal_code || prev.shipping.postalCode,
                          country: d.country_code || prev.shipping.country,
                        }
                      }));
                    }}
                    placeholder="Street"
                  />
                  <div className="account-modal-address-fields">
                    <input
                      className="p-2 border rounded"
                      placeholder="City"
                      value={form.shipping.city}
                      onChange={e => handleAddressChange('shipping', 'city', e.target.value)}
                    />
                    <select
                      value={form.shipping.state}
                      onChange={e => handleAddressChange('shipping', 'state', e.target.value)}
                    >
                      <option value="">Select State</option>
                      {US_STATE_OPTIONS.map(state => (
                        <option
                          key={state.code}
                          value={state.code}
                        >
                          {state.code} - {state.name}
                        </option>
                      ))}
                    </select>
                    <input
                      className="p-2 border rounded"
                      placeholder="Zip/Postal Code"
                      value={form.shipping.postalCode}
                      onChange={e => handleAddressChange('shipping', 'postalCode', e.target.value)}
                    />
                  </div>
                  <input
                    className="p-2 border rounded w-full bg-gray-100"
                    value={form.shipping.country}
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {!success && (
          <div className="account-modal-footer">
            <button
              type="submit"
              form={ACCOUNT_MODAL_FORM_ID}
              className="account-modal-btn-primary"
            >
              {submitLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function AccountManagement() {
  const { authFetch, isReady } = useAuthFetch();
  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentDisplayCount, setCurrentDisplayCount] = useState(INITIAL_LOAD_COUNT);
  const [sortConfig, setSortConfig] = useState({ key: 'Name', direction: 'asc' });
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [columnFilterValues, setColumnFilterValues] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const loadingRef = useRef(null);
  const tableScrollRef = useRef(null);
  const hScrollbarRef = useRef(null);
  const {
    filterDropdownRef,
    toggleFilterPopup,
    closeFilterPopup,
    renderFilterPopup,
    getFilterWrapperStyle,
  } = useFilterPopupPortal({
    activeFilterColumn,
    setActiveFilterColumn,
    tableScrollRef,
    hScrollbarRef,
  });
  const hScrollLastRef = useRef({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const [hScroll, setHScroll] = useState({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const columnSelectorRef = useRef(null);
  const columnButtonRef = useRef(null);
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [columnSelectorPosition, setColumnSelectorPosition] = useState({ top: 0, left: 0 });

  const {
    value: visibleColumns,
    setValue: setVisibleColumns,
    save: saveVisibleColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.ACCOUNTS_VISIBLE_COLUMNS,
    DEFAULT_VISIBLE_COLUMNS,
    {
      mergeWithDefault: mergeVisibleColumns,
      localStorageKey: 'accounts_visibleColumns',
    }
  );

  const {
    value: columnOrder,
    setValue: setColumnOrder,
    save: saveColumnOrder,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.ACCOUNTS_COLUMN_ORDER,
    DEFAULT_COLUMN_ORDER,
    {
      mergeWithDefault: mergeColumnOrder,
      localStorageKey: 'accounts_columnOrder',
    }
  );

  const {
    value: pinnedColumns,
    setValue: setPinnedColumns,
    save: savePinnedColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.ACCOUNTS_PINNED_COLUMNS,
    DEFAULT_PINNED_COLUMNS,
    {
      mergeWithDefault: mergePinnedColumns,
      localStorageKey: 'accounts_pinnedColumns',
    }
  );

  const columns = React.useMemo(() => {
    return [...originalColumns].sort((a, b) => {
      const aIdx = columnOrder.indexOf(a.key);
      const bIdx = columnOrder.indexOf(b.key);
      return (aIdx !== -1 ? aIdx : 99) - (bIdx !== -1 ? bIdx : 99);
    });
  }, [columnOrder]);

  const pinnedVisibleKeys = pinnedColumns.filter((key) => visibleColumns.includes(key));
  const unpinnedVisibleColumns = columns.filter(
    (column) => visibleColumns.includes(column.key) && !pinnedVisibleKeys.includes(column.key)
  );
  const visibleTableColumns = [
    ...pinnedVisibleKeys
      .map((key) => originalColumns.find((column) => column.key === key))
      .filter(Boolean),
    ...unpinnedVisibleColumns,
  ];
  const tableMinWidth = visibleTableColumns.reduce((sum, column) => sum + column.width, 0) || 1;
  const lastPinnedKey = pinnedVisibleKeys[pinnedVisibleKeys.length - 1] || null;

  const getPinnedLeftOffset = (columnKey) => {
    let left = 0;
    for (const key of pinnedVisibleKeys) {
      if (key === columnKey) return left;
      const col = originalColumns.find((c) => c.key === key);
      left += col?.width || 0;
    }
    return 0;
  };

  const pinnedColumnsWidth = pinnedVisibleKeys.reduce((sum, key) => {
    const col = originalColumns.find((c) => c.key === key);
    return sum + (col?.width || 0);
  }, 0);

  const handleTableScroll = (event) => {
    const bar = hScrollbarRef.current;
    if (!bar) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(bar.scrollLeft - next) >= 1) bar.scrollLeft = next;
  };

  const handleHScrollbarScroll = (event) => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(scroller.scrollLeft - next) >= 1) scroller.scrollLeft = next;
  };

  const handleTableWheelCapture = (event) => {
    if (!pinnedColumnsWidth) return;
    const pinnedEdgeEl = event.currentTarget.querySelector('.projects-col-pinned-last');
    if (!pinnedEdgeEl) return;
    const pinnedRight = pinnedEdgeEl.getBoundingClientRect().right;
    if (event.clientX > pinnedRight) return;
    const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
    if (!isHorizontal) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const getColumnStyle = (column, { isHeader = false } = {}) => {
    const isPinned = pinnedVisibleKeys.includes(column.key);
    if (isPinned) {
      return {
        width: `${column.width}px`,
        minWidth: `${column.width}px`,
        maxWidth: `${column.width}px`,
        position: 'sticky',
        left: `${getPinnedLeftOffset(column.key)}px`,
        zIndex: isHeader ? 30 : 4,
      };
    }
    return {
      minWidth: `${column.width}px`,
      width: `${(column.width / tableMinWidth) * 100}%`,
    };
  };

  const handleToggleColumn = (columnKey) => {
    const isHiding = visibleColumns.includes(columnKey);
    const newColumns = isHiding
      ? visibleColumns.filter((key) => key !== columnKey)
      : [...visibleColumns, columnKey];
    setVisibleColumns(newColumns);
    saveVisibleColumns(newColumns);

    if (isHiding && pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
    }
  };

  const handleTogglePin = (columnKey) => {
    if (pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
      return;
    }

    if (!visibleColumns.includes(columnKey)) {
      const nextVisible = [...visibleColumns, columnKey];
      setVisibleColumns(nextVisible);
      saveVisibleColumns(nextVisible);
    }

    const nextPins = [...pinnedColumns, columnKey];
    setPinnedColumns(nextPins);
    savePinnedColumns(nextPins);
  };

  const handleReorderColumn = (fromIndex, toIndex, groupColumns) => {
    const fromKey = groupColumns[fromIndex].key;
    const toKey = groupColumns[toIndex].key;

    setColumnOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const idxA = newOrder.indexOf(fromKey);
      const idxB = newOrder.indexOf(toKey);
      if (idxA !== -1 && idxB !== -1) {
        const [movedItem] = newOrder.splice(idxA, 1);
        newOrder.splice(idxB, 0, movedItem);
      }
      return newOrder;
    });
  };

  const handleReorderColumnEnd = () => {
    saveColumnOrder();
  };

  const handleColumnSelectorClick = () => {
    setIsColumnSelectorOpen((prev) => {
      const next = !prev;
      if (next && columnButtonRef.current) {
        const rect = columnButtonRef.current.getBoundingClientRect();
        const dropdownWidth = 208;
        let left = rect.left;
        let top = rect.bottom + 4;

        if (left + dropdownWidth > window.innerWidth - 8) {
          left = rect.right - dropdownWidth;
        }
        if (left < 8) {
          left = 8;
        }
        setColumnSelectorPosition({ top, left });
      }
      return next;
    });
  };

  const draggedHeaderKeyRef = useRef(null);
  const draggedHeaderWasPinnedRef = useRef(false);

  const handleHeaderDragStart = (e, columnKey) => {
    if (e.target.closest?.('[data-pin-toggle]')) {
      e.preventDefault();
      return;
    }
    draggedHeaderKeyRef.current = columnKey;
    draggedHeaderWasPinnedRef.current = pinnedColumns.includes(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleHeaderDragOver = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = draggedHeaderKeyRef.current;
    if (!draggedKey || draggedKey === targetKey) return;

    const draggedPinned = pinnedColumns.includes(draggedKey);
    const targetPinned = pinnedColumns.includes(targetKey);

    if (draggedPinned && targetPinned) {
      setPinnedColumns((prev) => {
        const next = [...prev];
        const idxA = next.indexOf(draggedKey);
        const idxB = next.indexOf(targetKey);
        if (idxA === -1 || idxB === -1) return prev;
        const [movedItem] = next.splice(idxA, 1);
        next.splice(idxB, 0, movedItem);
        return next;
      });
      return;
    }

    if (!draggedPinned && !targetPinned) {
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const idxA = newOrder.indexOf(draggedKey);
        const idxB = newOrder.indexOf(targetKey);
        if (idxA !== -1 && idxB !== -1) {
          const [movedItem] = newOrder.splice(idxA, 1);
          newOrder.splice(idxB, 0, movedItem);
        }
        return newOrder;
      });
    }
  };

  const handleHeaderDragEnd = () => {
    const wasPinned = draggedHeaderWasPinnedRef.current;
    draggedHeaderKeyRef.current = null;
    draggedHeaderWasPinnedRef.current = false;
    if (wasPinned) {
      savePinnedColumns();
    } else {
      saveColumnOrder();
    }
  };

  const clearStoredData = async () => {
    localStorage.removeItem('accounts_columnOrder');
    localStorage.removeItem('accounts_visibleColumns');
    localStorage.removeItem('accounts_pinnedColumns');

    setSearchTerm('');
    setSortConfig({ key: 'Name', direction: 'asc' });
    setColumnFilterValues({});
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);

    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setPinnedColumns(DEFAULT_PINNED_COLUMNS);

    await Promise.all([
      saveColumnOrder(DEFAULT_COLUMN_ORDER),
      saveVisibleColumns(DEFAULT_VISIBLE_COLUMNS),
      savePinnedColumns(DEFAULT_PINNED_COLUMNS),
    ]);

    window.location.reload();
  };

  useEffect(() => {
    if (!isColumnSelectorOpen) return undefined;
    function handleClickOutside(event) {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setIsColumnSelectorOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isColumnSelectorOpen]);

  const fetchAccounts = async () => {
    if (!isReady) return;
    setLoading(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/accounts`);
      const data = await response.json();
      const accountsData = data.data || [];
      setAllAccounts(accountsData);
      setCurrentDisplayCount(INITIAL_LOAD_COUNT);
      setHasMoreData(accountsData.length > INITIAL_LOAD_COUNT);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAllAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [isReady]);

  // Get filtered and sorted data
  const getFilteredAndSortedData = React.useCallback(() => {
    let filtered = allAccounts;

    if (searchTerm) {
      filtered = filtered.filter(acc =>
        originalColumns.some(col => {
          if (col.key.includes('.')) {
            const [parent, child] = col.key.split('.');
            return (acc[parent]?.[child] || '').toLowerCase().includes(searchTerm.toLowerCase());
          }
          return (acc[col.key] || '').toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    Object.entries(columnFilterValues).forEach(([key, values]) => {
      if (values && values.length > 0) {
        filtered = filtered.filter(acc => {
          if (key.includes('.')) {
            const [parent, child] = key.split('.');
            return values.includes(acc[parent]?.[child]);
          }
          
          // Special handling for date fields - compare formatted dates
          if (key === 'Project_Site_Acct_Last_Modified_Date__c') {
            const formatMMDDYYYY = (dateStr) => {
              if (!dateStr) return null;
              const d = new Date(dateStr);
              if (isNaN(d.getTime())) return null;
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${mm}/${dd}/${yyyy}`;
            };
            const formattedDate = formatMMDDYYYY(acc[key]);
            return values.includes(formattedDate);
          }
          
          return values.includes(acc[key]);
        });
      }
    });

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      if (sortConfig.key.includes('.')) {
        const [parent, child] = sortConfig.key.split('.');
        const aValue = (a[parent]?.[child] || '').toLowerCase();
        const bValue = (b[parent]?.[child] || '').toLowerCase();
        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      }

      // Special handling for date fields
      if (sortConfig.key === 'Project_Site_Acct_Last_Modified_Date__c') {
        const aTime = a[sortConfig.key] ? new Date(a[sortConfig.key]).getTime() : 0;
        const bTime = b[sortConfig.key] ? new Date(b[sortConfig.key]).getTime() : 0;
        
        // If both are empty, they're equal
        if (aTime === 0 && bTime === 0) return 0;
        // Empty dates go to the end
        if (aTime === 0) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bTime === 0) return sortConfig.direction === 'asc' ? -1 : 1;
        
        if (sortConfig.direction === 'asc') {
          return aTime - bTime;
        } else {
          return bTime - aTime;
        }
      }

      // Handle null/empty values properly
      const aValue = (a[sortConfig.key] || '').toLowerCase();
      const bValue = (b[sortConfig.key] || '').toLowerCase();
      
      // If both are empty, they're equal
      if (!aValue && !bValue) return 0;
      // Empty values go to the end
      if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
      if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return sorted;
  }, [allAccounts, searchTerm, columnFilterValues, sortConfig]);

  // Format date for display
  const formatMMDDYYYY = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // Get column display value for export
  const getColumnDisplayValue = (account, column) => {
    if (!account || !column) return '-';

    if (column.key.includes('.')) {
      const [parent, child] = column.key.split('.');
      const value = account[parent]?.[child];
      return value !== undefined && value !== null && value !== '' ? value : '-';
    }

    if (column.key === 'Project_Site_Acct_Last_Modified_Date__c') {
      const value = account[column.key];
      return value ? formatMMDDYYYY(value) : '-';
    }

    const directValue = account[column.key];
    if (directValue === undefined || directValue === null || directValue === '') return '-';

    return directValue;
  };

  const handleExportToExcel = async () => {
    if (isExporting) return;

    if (columns.length === 0) {
      console.warn('No columns selected for export');
      return;
    }

    const exportColumns = columns.filter((column) => visibleColumns.includes(column.key));
    if (exportColumns.length === 0) {
      console.warn('No visible columns selected for export');
      return;
    }

    try {
      setIsExporting(true);
      const XLSX = await loadXLSX();
      if (!XLSX) {
        console.error('XLSX library not available');
        return;
      }

      const headers = exportColumns.map(column => column.label);
      const exportData = getFilteredAndSortedData();
      const rows = exportData.map(account =>
        exportColumns.map(column => getColumnDisplayValue(account, column))
      );

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Accounts');
      XLSX.writeFile(workbook, 'accounts.xlsx');
    } catch (error) {
      console.error('Failed to export accounts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const loadMoreData = React.useCallback(() => {
    if (loadingMore || !hasMoreData || loading) return;
    
    setLoadingMore(true);
    
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      setTimeout(() => {
        const filteredData = getFilteredAndSortedData();
        setCurrentDisplayCount(prev => {
          const newCount = Math.min(prev + ITEMS_PER_PAGE, filteredData.length);
          const hasMore = newCount < filteredData.length;
          setHasMoreData(hasMore);
          return newCount;
        });
        setLoadingMore(false);
      }, 300);
    });
  }, [loadingMore, hasMoreData, loading, getFilteredAndSortedData]);

  // Intersection Observer for infinite scroll (root = table scroller, same as Projects/Contacts)
  useEffect(() => {
    if (loading || loadingMore || !hasMoreData) return;

    const root = tableScrollRef.current;
    const sentinel = loadingRef.current;
    if (!root || !sentinel) return;

    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (cancelled) return;
        if (entries[0]?.isIntersecting) {
          loadMoreData();
        }
      },
      { root, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [loading, loadingMore, hasMoreData, currentDisplayCount, loadMoreData]);

  // Update getUniqueValues to handle nested objects (for Parent.Name) and date fields
  const getUniqueValues = (data, key) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      return [...new Set(data.map(item => item[parent]?.[child] || '').filter(Boolean))].sort();
    }
    
    // Special handling for date fields - format as MM/DD/YYYY for filter options
    if (key === 'Project_Site_Acct_Last_Modified_Date__c') {
      const formatMMDDYYYY = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
      };
      return [...new Set(data.map(item => formatMMDDYYYY(item[key])))]
        .filter(Boolean)
        .sort();
    }
    
    return [...new Set(data.map(item => item[key] || '').filter(Boolean))].sort();
  };

  // Update displayed accounts based on current display count
  const displayedAccounts = React.useMemo(() => {
    const filteredData = getFilteredAndSortedData();
    const sliced = filteredData.slice(0, currentDisplayCount);
    
    // Update hasMoreData based on current filtered data
    const hasMore = currentDisplayCount < filteredData.length;
    if (hasMoreData !== hasMore) {
      setHasMoreData(hasMore);
    }
    
    return sliced;
  }, [getFilteredAndSortedData, currentDisplayCount, hasMoreData]);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const clientWidth = el.clientWidth;
      const scrollWidth = el.scrollWidth;
      const overflow = Math.max(0, scrollWidth - clientWidth);
      const last = hScrollLastRef.current;
      if (
        Math.abs(overflow - last.overflow) < 1 &&
        Math.abs(scrollWidth - last.scrollWidth) < 1 &&
        Math.abs(clientWidth - last.clientWidth) < 1
      ) {
        return;
      }
      hScrollLastRef.current = { overflow, scrollWidth, clientWidth };
      setHScroll({ overflow, scrollWidth, clientWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tableMinWidth, pinnedColumnsWidth, loading, displayedAccounts.length]);

  // Total filtered count for indicator
  const totalFilteredCount = React.useMemo(() => {
    return getFilteredAndSortedData().length;
  }, [getFilteredAndSortedData]);

  // Sorting
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
    // Reset display count when sorting changes
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  // Filter dropdown handlers
  const toggleColumnFilter = (columnName, event) => {
    toggleFilterPopup(columnName, event);
  };
  const handleColumnFilterSelect = (columnName, value) => {
    setColumnFilterValues(prev => ({
      ...prev,
      [columnName]: prev[columnName]?.includes(value)
        ? prev[columnName].filter(v => v !== value)
        : [...(prev[columnName] || []), value]
    }));
    // Reset display count when filter changes
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };
  const clearColumnFilter = (key) => {
    setColumnFilterValues(prev => ({ ...prev, [key]: [] }));
    // Reset display count when filter is cleared
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  const handleRowClick = (account) => {
    setSelectedRowId(account.Id);
    setSelectedAccount(account);
    setShowAddModal(true);
  };

  return (
    <div className="projects-page-card">
      <div className="projects-table-toolbar">
        <div className="projects-table-toolbar-row flex items-center gap-4 max-md:gap-2">
          <form className="projects-toolbar-search flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-white border rounded-[31px] text-zinc-500 w-[30%] shadow-sm max-md:flex-1 max-md:w-auto max-md:bg-stone-50 max-md:border-0 max-md:rounded-full max-md:shadow-none">
            <img
              loading="lazy"
              src={Icon13}
              className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]"
              alt=""
            />
            <input
              type="search"
              placeholder="Search"
              className="bg-transparent border-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                setHasMoreData(true);
              }}
            />
          </form>

          <button
            onClick={() => setShowAddModal(true)}
            disabled={loading}
            className={`ml-2 px-6 py-2 rounded transition-colors max-md:hidden ${
              loading
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-red-700 text-white hover:bg-red-800'
            }`}
          >
            + Add Account
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={loading}
            aria-label="Add Account"
            className={`projects-toolbar-add hidden max-md:flex items-center justify-center rounded-full w-10 h-10 ${
              loading ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-red-700 text-white'
            }`}
          >
            <FiPlus className="w-5 h-5" />
          </button>

          <div className="flex-grow max-md:hidden"></div>

          <div className="projects-toolbar-actions">
            <div className="relative projects-toolbar-columns">
              <button
                type="button"
                ref={columnButtonRef}
                onClick={handleColumnSelectorClick}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none"
                title="Show/Hide Columns"
              >
                <FiColumns className="h-4 w-4" />
                <span className="ml-2 max-md:hidden">Columns</span>
              </button>
              <ColumnVisibilityDropdown
                ref={columnSelectorRef}
                isOpen={isColumnSelectorOpen}
                onClose={() => setIsColumnSelectorOpen(false)}
                columns={columns}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                onReorderColumn={handleReorderColumn}
                onReorderEnd={handleReorderColumnEnd}
                position={columnSelectorPosition}
              />
            </div>

            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={loading || displayedAccounts.length === 0 || isExporting}
              className={`projects-toolbar-export flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-2 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none max-md:mr-0 ${
                loading || displayedAccounts.length === 0 || isExporting ? 'cursor-not-allowed opacity-60' : ''
              }`}
              title="Download Excel"
            >
              <FiDownload className="h-4 w-4" />
            </button>

            <ResetButton onClick={clearStoredData} tooltip="Reset" />
          </div>
        </div>

        <AddAccountModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setSelectedAccount(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setSelectedAccount(null);
            setCurrentDisplayCount(INITIAL_LOAD_COUNT);
            setHasMoreData(true);
            setLoadingMore(false);
            fetchAccounts();
          }}
          account={selectedAccount}
        />
      </div>

      <div
        ref={tableScrollRef}
        className="projects-table-scroll projects-table-scroll--proxy-h"
        onScroll={handleTableScroll}
        onWheelCapture={handleTableWheelCapture}
      >
        <table
          className="projects-data-table"
          style={{
            width: `max(100%, ${tableMinWidth}px)`,
            minWidth: `${tableMinWidth}px`,
          }}
        >
          <colgroup>
            {visibleTableColumns.map((column) => {
              const isPinned = pinnedVisibleKeys.includes(column.key);
              return (
                <col
                  key={column.key}
                  style={
                    isPinned
                      ? {
                          width: `${column.width}px`,
                          minWidth: `${column.width}px`,
                        }
                      : {
                          width: `${(column.width / tableMinWidth) * 100}%`,
                          minWidth: `${column.width}px`,
                        }
                  }
                />
              );
            })}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {visibleTableColumns.map((column) => {
                const isPinned = pinnedVisibleKeys.includes(column.key);
                return (
                  <th
                    key={column.key}
                    style={getColumnStyle(column, { isHeader: true })}
                    className={`group/header px-4 py-3 max-md:whitespace-normal cursor-grab active:cursor-grabbing transition-colors hover:bg-gray-100 ${
                      isPinned ? 'projects-col-pinned' : ''
                    } ${column.key === lastPinnedKey ? 'projects-col-pinned-last' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleHeaderDragStart(e, column.key)}
                    onDragOver={(e) => handleHeaderDragOver(e, column.key)}
                    onDragEnd={handleHeaderDragEnd}
                  >
                    <div className="flex flex-col gap-2">
                      <div
                        className="flex items-center justify-center text-sm font-medium text-gray-500 cursor-pointer whitespace-nowrap"
                        onClick={() => handleSort(column.key)}
                      >
                        <span className="truncate" title={column.label}>{column.label}</span>
                        <span className="ml-1 inline-flex w-4 shrink-0 items-center justify-center text-gray-500">
                          <SortIcon direction={sortIconDirection(sortConfig, column.key)} />
                        </span>
                        <button
                          type="button"
                          data-pin-toggle
                          title={isPinned ? 'Unpin column' : 'Pin column to left'}
                          aria-label={isPinned ? `Unpin ${column.label}` : `Pin ${column.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePin(column.key);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          draggable={false}
                          className={`projects-pin-toggle ml-1 p-0.5 rounded shrink-0 transition-opacity ${
                            isPinned
                              ? 'is-pinned text-red-600 hover:text-red-800 hover:bg-red-50'
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
                          }`}
                        >
                          <span className="inline-flex">
                            {isPinned ? (
                              <MdPushPin className="h-3.5 w-3.5" />
                            ) : (
                              <MdOutlinePushPin className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </button>
                      </div>
                      <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                        {column.filterable && (
                          <div
                            className="relative"
                            ref={activeFilterColumn === column.key ? filterDropdownRef : null}
                            style={getFilterWrapperStyle(activeFilterColumn === column.key)}
                          >
                            <button
                              onClick={(e) => toggleColumnFilter(column.key, e)}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${
                                columnFilterValues[column.key]?.length > 0 ? 'text-red-600 border-red-500' : 'text-zinc-500'
                              }`}
                            >
                              <span>
                                {columnFilterValues[column.key]?.length > 0
                                  ? `${columnFilterValues[column.key].length} selected`
                                  : 'Filter'}
                              </span>
                              <FiFilter className="ml-2" />
                            </button>
                            {activeFilterColumn === column.key &&
                              renderFilterPopup(
                                <ColumnFilterDropdown
                                  isOpen={activeFilterColumn === column.key}
                                  options={getUniqueValues(allAccounts, column.key)}
                                  selectedValues={columnFilterValues[column.key] || []}
                                  onSelect={(value) => handleColumnFilterSelect(column.key, value)}
                                  onClose={closeFilterPopup}
                                />
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700" />
                    <span className="ml-2">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : displayedAccounts.length === 0 ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="text-center py-4">No accounts found</td>
              </tr>
            ) : (
              displayedAccounts.map((acc, idx) => (
                <tr
                  key={acc.Id || idx}
                  className={`hover:bg-gray-50 cursor-pointer ${acc.Id === selectedRowId ? 'bg-red-50' : ''}`}
                  onClick={() => handleRowClick(acc)}
                >
                  {visibleTableColumns.map((col) => {
                    const isPinned = pinnedVisibleKeys.includes(col.key);
                    let content;
                    if (col.key.includes('.')) {
                      const [parent, child] = col.key.split('.');
                      content = acc[parent]?.[child] || '-';
                    } else {
                      content = acc[col.key] || '-';
                    }

                    return (
                      <TooltipCell
                        key={col.key}
                        content={col.key === 'Project_Site_Acct_Last_Modified_Date__c' ? formatFullTimestamp(content) : content}
                        className={`text-gray-900 max-md:whitespace-normal break-words${
                          isPinned ? ' projects-col-pinned' : ''
                        }${col.key === lastPinnedKey ? ' projects-col-pinned-last' : ''}`}
                        align="left"
                        style={getColumnStyle(col)}
                      />
                    );
                  })}
                </tr>
              ))
            )}
            {!loading && displayedAccounts.length > 0 && hasMoreData && (
              <tr ref={loadingRef}>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    {loadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-700" />
                        <span className="ml-2">Loading more...</span>
                      </>
                    ) : (
                      <span className="text-gray-500">Scroll down to load more</span>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {!loading && displayedAccounts.length > 0 && !hasMoreData && (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    <span className="text-gray-500">No more data to load</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hScroll.overflow > 0 && (
        <div className="projects-h-scrollbar-rail">
          <div
            ref={hScrollbarRef}
            onScroll={handleHScrollbarScroll}
            className="projects-h-scrollbar"
            style={{
              marginLeft: `${pinnedColumnsWidth}px`,
              width: `${Math.max(hScroll.clientWidth - pinnedColumnsWidth, 0)}px`,
            }}
          >
            <div style={{ height: '1px', width: `${Math.max(hScroll.scrollWidth - pinnedColumnsWidth, 0)}px` }} />
          </div>
        </div>
      )}
      <RowsIndicator loadedCount={displayedAccounts.length} totalCount={totalFilteredCount} />
    </div>
  );
} 
